import React, { useState, useReducer } from 'react';
import swal from 'sweetalert2';
import SignupAction from 'actions/AuthenticationAction';
import InputComponent from 'components/shared/InputComponent';
import InputPhoneCode from 'components/shared/InputPhoneCode';
import * as Validations from 'validations/HomieValidations';
import { FlexColumn } from 'styles/Layout';
import { PrimaryButton } from 'styles/Buttons';
import {
  SignUpFormContainer,
  SignUpFormColumn,
  StyledHeader,
  TaCParagraph,
  SignUpWrapper,
  SensitiveDataWarning,
  TaCLink
} from './styles';

type SignUpFormProps = {
  visible: boolean,
  showLoadingProcess: (msg: string) => null,
  hideLoadingProcess: () => null
};

const defaultState = {
  name: '',
  lastName: '',
  secondLastName: '',
  email: '',
  password: '',
  countryCode: 52,
  mobilePhone: ''
};
const reducer = (state, action) => {
  switch (action.type) {
    case 'name':
    case 'lastName':
    case 'secondLastName':
    case 'email':
    case 'password':
    case 'countryCode':
    case 'mobilePhone':
      return { ...state, [action.type]: action.payload };
    default:
      break;
  }
};

const SignUpForm = ({
  visible = false,
  showLoadingProcess,
  hideLoadingProcess
}: SignUpFormProps) => {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const [isCountryCodeVisible, setCountryCodeVisible] = useState(false);
  const [shouldRenderForm, setShouldRenderForm] = useState(
    window.innerWidth > 767
  );

  const showForm = () => setShouldRenderForm(true);

  const validateFormAndSubmit = e => {
    e.preventDefault();
    new Promise(resolve => {
      document
        .querySelectorAll('#external-owner-signup-form input')
        .forEach(input => {
          input.focus();
          input.blur();
        });
      setTimeout(() => resolve(), 300);
    }).then(() => {
      if (
        document.querySelectorAll('#external-owner-signup-form .error-message')
          .length !== 0
      ) {
        return;
      }

      showLoadingProcess('Guardando datos...');
      const {
        countryCode,
        mobilePhone,
        name,
        lastName,
        secondLastName,
        email,
        password
      } = state;
      const data = {
        user: {
          name,
          last_name: lastName,
          second_last_name: secondLastName,
          email,
          mobile_phone: `+${countryCode}${mobilePhone}`,
          password,
          password_confirmation: password,
          is_suscribed: true,
          termns_and_conditions: true
        },
        from_axios: true,
        error: false,
        role: 'user'
      };
      SignupAction.registerUser(
        data,
        false,
        () => {
          hideLoadingProcess();
        },
        e => {
          hideLoadingProcess();
          swal({
            title: '¡Ha ocurrido un error!',
            text:
              (e.responseJson && e.responseJSON.message) ||
              'Ocurrió un error desconocido mientras se guardaban los datos',
            type: 'error'
          });
        }
      );
    });
  };

  const renderMobileStep = () => (
    <React.Fragment>
      <SensitiveDataWarning>
        El reporte completo contiene datos sensibles, para mayor seguridad
        regístrate para ver el reporte completo
      </SensitiveDataWarning>
      <FlexColumn alignItems="center">
        <PrimaryButton style={{ width: 300 }} onClick={showForm}>
          Registrarme
        </PrimaryButton>
      </FlexColumn>
    </React.Fragment>
  );

  const renderForm = () => (
    <form id="external-owner-signup-form">
      <StyledHeader marginBottom={40} marginTop={40} align="center">
        Para ver el reporte completo es necesario generar un registro
      </StyledHeader>
      <SignUpFormContainer>
        <SignUpFormColumn>
          <InputComponent
            name="name"
            value={state.name}
            type="text"
            placeholder="Nombre"
            onValueChange={value => dispatch({ type: 'name', payload: value })}
            schema={Validations.RequiredFieldSchema}
            fontSize={14}
            marginBottom={17}
          />
          <InputComponent
            name="lastName"
            value={state.lastName}
            type="text"
            placeholder="Apellido Paterno"
            onValueChange={value =>
              dispatch({ type: 'lastName', payload: value })
            }
            schema={Validations.RequiredFieldSchema}
            fontSize={14}
            marginBottom={17}
          />
          <InputComponent
            name="secondLastName"
            value={state.secondLastName}
            type="text"
            placeholder="Apellido Materno"
            onValueChange={value =>
              dispatch({ type: 'secondLastName', payload: value })
            }
            schema={Validations.RequiredFieldSchema}
            fontSize={14}
            marginBottom={17}
          />
        </SignUpFormColumn>
        <SignUpFormColumn>
          <InputPhoneCode
            type="text"
            placeholder="Número de whatsapp"
            name="mobilePhone"
            value={state.mobilePhoe}
            pickingCountryCode={isCountryCodeVisible}
            countryCode={state.countryCode}
            handleCountryCodeClick={() =>
              setCountryCodeVisible(!isCountryCodeVisible)
            }
            handleCountryCodePicked={code => {
              dispatch({
                type: 'countryCode',
                payload: parseInt(code.value, 10)
              });
              setCountryCodeVisible(false);
            }}
            onValueChange={value =>
              dispatch({ type: 'mobilePhone', payload: value })
            }
            schema={Validations.PhoneNumberSchema}
            fontSize={14}
            marginBottom={17}
            hasTop={false}
          />
          <InputComponent
            name="email"
            value={state.email}
            type="text"
            placeholder="Correo Electrónico"
            onValueChange={value => dispatch({ type: 'email', payload: value })}
            schema={Validations.RequiredEmailSchema}
            fontSize={14}
            marginBottom={17}
          />
          <InputComponent
            name="password"
            value={state.password}
            type="password"
            placeholder="Contraseña"
            onValueChange={value =>
              dispatch({ type: 'password', payload: value })
            }
            schema={Validations.RequiredFieldSchema}
            fontSize={14}
            marginBottom={17}
          />
        </SignUpFormColumn>
      </SignUpFormContainer>
      <FlexColumn alignItems="center">
        <TaCParagraph>
          Al dar click en continuar, estoy de acuerdo con el{' '}
          <TaCLink to="/legal/aviso-de-privacidad">Aviso de Privacidad</TaCLink>{' '}
          y{' '}
          <TaCLink to="/legal/terminos-y-condiciones-generales-homie">
            Términos y Condiciones
          </TaCLink>
        </TaCParagraph>
        <PrimaryButton style={{ width: 300 }} onClick={validateFormAndSubmit}>
          Registrarme
        </PrimaryButton>
      </FlexColumn>
    </form>
  );

  return (
    <SignUpWrapper visible={visible}>
      {shouldRenderForm && renderForm()}
      {!shouldRenderForm && renderMobileStep()}
    </SignUpWrapper>
  );
};

export default SignUpForm;
